import { useEffect, useRef } from 'react';
import { bannerKind } from '../services/installPrompt';
import { trackAction } from '../services/telemetry';
import { useInstall } from '../state/install';

/** iOS Safari's Share glyph: a square with an up arrow. */
export function ShareIcon({ size = 18 }: { size?: number }) {
  return (
    <svg className="share-icon" role="img" aria-label="Share" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="M8 7l4-4 4 4" />
      <path d="M8 10H6a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9a1 1 0 0 0-1-1h-2" />
    </svg>
  );
}

/** Invites the player to install: Chromium's native dialog, or Add to Home Screen steps on iOS Safari. Sits in flow above the nav. */
export function InstallBanner() {
  const kind = useInstall((s) =>
    // `installed` already covers standalone mode; the store seeds it from display-mode and navigator.standalone.
    bannerKind({ standalone: false, installed: s.installed, hasDeferredPrompt: s.deferred != null, platform: s.platform, dismissed: s.dismissed }),
  );
  const shown = useRef<typeof kind>(null);

  useEffect(() => {
    if (kind && shown.current !== kind) trackAction('install_prompt_shown', { kind });
    shown.current = kind;
  }, [kind]);

  if (!kind) return null;

  const install = async () => {
    const outcome = await useInstall.getState().prompt();
    if (outcome === 'accepted') trackAction('install_prompt_accepted', { kind });
    else if (outcome === 'dismissed') trackAction('install_prompt_dismissed', { kind, source: 'native' });
  };
  const dismiss = () => {
    useInstall.getState().dismiss();
    trackAction('install_prompt_dismissed', { kind, source: 'banner' });
  };

  return (
    <section className="install-banner" aria-label="Install EthrHunt">
      {kind === 'native' ? (
        <>
          <p className="install-banner-text">📲 Install EthrHunt</p>
          <button type="button" className="btn filled" onClick={() => void install()}>Install</button>
        </>
      ) : (
        <p className="install-banner-text">
          Tap Share <ShareIcon />, then Add to Home Screen
        </p>
      )}
      <button type="button" className="install-banner-close" aria-label="Dismiss install prompt" onClick={dismiss}>×</button>
    </section>
  );
}
